import { Box, Typography, Paper } from "@mui/material";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

const agents = [
  {
    name: "Agente Literatura",
    tag: "Pesquisa Científica",
    description:
      "Varre PubMed, Cochrane e bases ANVISA em tempo real, priorizando meta-análises e ECRs de alta qualidade.",
    time: "~0.8s",
    accent: "#FF8904",
    icon: ScienceOutlinedIcon,
  },
  {
    name: "Agente Diagnóstico",
    tag: "Hipóteses Clínicas",
    description:
      "Analisa sintomas e comorbidades para sugerir diagnósticos diferenciais e indicações terapêuticas.",
    time: "~0.6s",
    accent: "#a78bfa",
    icon: PsychologyOutlinedIcon,
  },
  {
    name: "Agente Farmacologia",
    tag: "Dosagem & Formulação",
    description:
      "Calcula doses individualizadas considerando peso, metabolismo e histórico farmacológico do paciente.",
    time: "~0.5s",
    accent: "#2dd4bf",
    icon: MedicationOutlinedIcon,
  },
  {
    name: "Agente Interações",
    tag: "Segurança Farmacológica",
    description:
      "Detecta interações críticas via CYP450 e alerta sobre contraindicações absolutas em tempo real.",
    time: "~0.4s",
    accent: "#f87171",
    icon: WarningAmberOutlinedIcon,
  },
  {
    name: "Agente Protocolos",
    tag: "Guidelines Clínicos",
    description:
      "Recupera e adapta protocolos validados por sociedades médicas e guidelines internacionais.",
    time: "~0.7s",
    accent: "#60a5fa",
    icon: DescriptionOutlinedIcon,
  },
  {
    name: "Agente Evidências",
    tag: "Nível de Evidência",
    description:
      "Classifica o grau de evidência científica (Oxford/GRADE) e gera score de confiança da resposta.",
    time: "~0.3s",
    accent: "#f472b6",
    icon: BarChartOutlinedIcon,
  },
  {
    name: "Agente Dosagem",
    tag: "Titulação & Ajuste",
    description:
      "Gera esquemas de titulação personalizados com janelas de reavaliação e critérios de sucesso.",
    time: "~0.6s",
    accent: "#FF8904",
    icon: MonitorHeartOutlinedIcon,
  },
  {
    name: "Agente Segurança",
    tag: "Vigilância & Alertas",
    description:
      "Monitora perfil de segurança, efeitos adversos documentados e populações de risco especial.",
    time: "~0.4s",
    accent: "#a78bfa",
    icon: ShieldOutlinedIcon,
  },
];

export default function AgentsShowcaseSection() {
  return (
    <section id="especialistas" className="relative z-10 px-6 md:px-12 pb-20 md:pb-28">
      <div className="text-center mb-14">
        {/* TODO: título exato desta seção não foi confirmado no design original — revisar copy antes de publicar */}
        <Typography
          variant="h4"
          sx={{
            color: "#ffffff",
            fontWeight: 700,
            fontFamily: "Oxanium, sans-serif",
            letterSpacing: "-0.02em",
            fontSize: { xs: "1.5rem", md: "2rem" },
          }}
        >
          Nossos agentes especialistas
        </Typography>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto">
        {agents.map((agent) => (
          <Paper
            key={agent.name}
            elevation={0}
            sx={{
              background:
                "linear-gradient(135deg, rgba(26, 49, 60, 0.6) 0%, rgba(4, 9, 10, 0.8) 100%)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "24px",
              padding: "24px",
              transition: "all 0.3s ease",
              "&:hover": {
                border: `1px solid ${agent.accent}40`,
                transform: "translateY(-4px)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${agent.accent}1A`,
                }}
              >
                <agent.icon sx={{ fontSize: 24, color: agent.accent }} />
              </Box>
              <div className="flex items-center gap-1.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full px-2 py-0.5 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>
                Online
              </div>
            </Box>

            <Typography
              sx={{
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "1.05rem",
                fontFamily: "Oxanium, sans-serif",
                marginBottom: "4px",
              }}
            >
              {agent.name}
            </Typography>
            <Typography
              sx={{
                color: agent.accent,
                fontWeight: 600,
                fontSize: "0.8rem",
                marginBottom: "10px",
              }}
            >
              {agent.tag}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "0.875rem",
                lineHeight: 1.6,
                marginBottom: "16px",
              }}
            >
              {agent.description}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: "rgba(255,255,255,0.4)" }}>
              <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: "0.75rem" }}>
                Resposta em {agent.time}
              </Typography>
            </Box>
          </Paper>
        ))}
      </div>

      <div className="text-center mt-10">
        <Typography
          component="p"
          sx={{
            color: "#FF8904",
            fontWeight: 700,
            fontSize: "0.75rem",
            letterSpacing: "0.15em",
          }}
        >
          DEMO AO VIVO
        </Typography>
      </div>
    </section>
  );
}
